import React, { useEffect, useRef } from 'react';

/**
 * Adds `is-visible` the first time the element scrolls into view. That class
 * drives every CSS reveal in styles/marketing.css ([data-pnp-reveal] rises,
 * kinetic headline words, growing claw slashes, eyebrow bars).
 * Without IntersectionObserver the class is added immediately, so content
 * is never stuck hidden.
 */
export default function Reveal({
  as: Tag = 'div',
  children,
  className = '',
  delay = 0,
  rise = true,
  style,
  threshold = 0.18,
  id,
  ...rest
}) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    if (typeof IntersectionObserver === 'undefined') {
      el.classList.add('is-visible');
      return undefined;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            el.classList.add('is-visible');
            io.disconnect();
          }
        });
      },
      { threshold, rootMargin: '0px 0px -6% 0px' },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [threshold]);

  const delayVars = delay ? { '--reveal-delay': `${delay}ms`, '--kh-delay': `${delay}ms` } : null;
  return (
    <Tag
      ref={ref}
      id={id}
      className={`pnp-reveal ${className}`.trim()}
      data-pnp-reveal={rise ? '' : undefined}
      style={{ ...delayVars, ...style }}
      {...rest}
    >
      {children}
    </Tag>
  );
}
